import React, { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { useNavigate } from "react-router-dom";
import { useTran } from "@/hooks";
import { ClipboardSignature, Plus, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHeader } from "@/components/common";
import { SelectField } from "@/components/common";
import SurveyLibraryTable from "../components/SurveyLibraryTable";
import { PATHS } from "@/lib/router";
import { useSurveyLibraryStore } from "../hooks/useSurveyLibraryStore";
import ShareSurveyDialog from "../components/ShareSurveyDialog";
import SurveyPreviewModal from "../components/SurveyPreviewModal";
import { Can } from "@/hooks/useCan/Can";
import { useAuthStore } from "@/features/auth/hooks";

const SURVEY_TYPES = [
  { value: "all", label: "All types" },
  { value: "prospect", label: "Prospect" },
  { value: "intake", label: "Intake" },
  { value: "feedback", label: "Feedback" },
  { value: "assessment", label: "Assessment" },
];

const SURVEY_STATUSES = [
  { value: "all", label: "All statuses" },
  { value: "active", label: "Active" },
  { value: "draft", label: "Draft" },
  { value: "closed", label: "Closed" },
  { value: "expired", label: "Expired" },
];

const SORT_OPTIONS = [
  { value: "recently_updated", label: "Recently updated" },
  { value: "oldest", label: "Oldest" },
  { value: "alphabetical", label: "Alphabetical" },
  { value: "most_submissions", label: "Most submissions" },
];

const SurveyLibraryPage = observer(() => {
  const { t } = useTran();
  const store = useSurveyLibraryStore();
  const [previewSurvey, setPreviewSurvey] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const permissions = user?.permissions || [];
  useEffect(() => {
    store.initialize();
  }, []);
  useEffect(() => {
    if (!store.initializing) {
      store.fetchSurveys();
    }
  }, [
    store.pagination.page,
    store.pagination.limit,
    store.filters.search,
    store.filters.type,
    store.filters.status,
    store.filters.sort,
  ]);

  return (
    <div className="space-y-6">
      {/* ── Header ── */}
      <PageHeader
        title={t("Survey Library")}
        description={
          !store.initializing
            ? `${store.stats.total} surveys · ${store.stats.active} active · ${store.stats.submissions} total submissions`
            : t("Manage your survey library.")
        }
        icon={ClipboardSignature}
        actions={
          <Can
            resource="survey_library"
            action="create"
            userPermissions={permissions}
          >
            <Button
              type="button"
              size="sm"
              className="gap-2 bg-emerald-600 hover:bg-emerald-700"
              onClick={() => navigate(PATHS.CREATE_NEW_SURVEY)}
            >
              <Plus className="h-4 w-4" />
              {t("Create new survey")}
            </Button>
          </Can>
        }
      />

      {/* ── Toolbar ── */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            className="pl-9 h-9 text-sm"
            placeholder={t("Search surveys...")}
            value={store.filters.search}
            onChange={(e) => store.setSearch(e.target.value)}
          />
        </div>

        <SelectField
          value={store.filters.type}
          onChange={(e) => store.setType(e.target.value)}
          options={SURVEY_TYPES}
          className="h-9 text-sm w-36"
        />

        <SelectField
          value={store.filters.status}
          onChange={(e) => store.setStatus(e.target.value)}
          options={SURVEY_STATUSES}
          className="h-9 text-sm w-36"
        />

        <div className="ml-auto">
          <SelectField
            value={store.filters.sort}
            onChange={(e) => store.setDropdownSort(e.target.value)}
            options={SORT_OPTIONS}
            className="h-9 text-sm w-48"
          />
        </div>
      </div>

      {/* ── Table ── */}
      <SurveyLibraryTable
        surveys={store.surveys}
        loading={store.initializing || store.loading}
        page={store.pagination.page}
        pageSize={store.pagination.limit}
        total={store.paginationInfo.total}
        onPageChange={(page) => store.setPage(page)}
        onPageSizeChange={(size) => store.setRowsPerPage(size)}
        sort={store.sort}
        onSort={(sort) => store.setSort(sort)}
        onEdit={(survey) =>
          navigate(PATHS.EDIT_SURVEY.replace(":surveyId", survey?._id || ""))
        }
        onClose={(id) => store.closeSurvey(id)}
        onDelete={(id) => store.deleteSurvey(id)}
        onPreview={(survey) => setPreviewSurvey(survey)}
      />
      <ShareSurveyDialog />
      <SurveyPreviewModal
        open={!!previewSurvey}
        survey={previewSurvey}
        onClose={() => setPreviewSurvey(null)}
      />
    </div>
  );
});

export default SurveyLibraryPage;
